import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getCloudPolicies, updateCloudPolicies, revokeCloudAi, formatApiError,
  getCloudProjects, createCloudProject, deleteCloudProject,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SlidersHorizontal, Save, ShieldOff, Sparkles, Loader2, FolderKanban, Plus, Trash2 } from "lucide-react";

const DIRECTIONS = [
  { v: "none", l: "Aucune synchronisation" },
  { v: "cloud_to_progere", l: "Cloud → PROGÈRE" },
  { v: "progere_to_cloud", l: "PROGÈRE → Cloud" },
  { v: "bidirectional", l: "Bidirectionnelle" },
];
const CONFLICTS = [
  { v: "manual_review", l: "Demander une confirmation" },
  { v: "newest_wins", l: "Version la plus récente" },
  { v: "progere_wins", l: "Version PROGÈRE" },
  { v: "cloud_wins", l: "Version Cloud" },
  { v: "keep_both", l: "Conserver les deux" },
];
const MODULE_LABELS = {
  CORE: "CORE", ERP: "ERP", PUNCH: "PUNCH", CRM: "CRM", COMPTA: "COMPTA",
  PAIE: "PAIE", DEVIS_AI: "DEVIS AI", TAKE_OFF: "TAKE OFF", SUPPORT: "SUPPORT", MOBILE: "MOBILE",
};

export default function CloudSyncPolicies({ canManage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [projects, setProjects] = useState([]);
  const [newProj, setNewProj] = useState({ name: "", code: "" });
  const [addingProj, setAddingProj] = useState(false);

  const load = async () => {
    try {
      const [pol, projs] = await Promise.all([getCloudPolicies("onedrive"), getCloudProjects()]);
      setRows(pol.policies || []);
      setProjects(projs || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addProject = async () => {
    if (!newProj.name.trim()) return;
    setAddingProj(true);
    try {
      await createCloudProject(newProj.name.trim(), newProj.code.trim() || undefined);
      setNewProj({ name: "", code: "" });
      const projs = await getCloudProjects();
      setProjects(projs || []);
      toast.success("Projet ajouté.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddingProj(false);
    }
  };

  const removeProject = async (id) => {
    try {
      await deleteCloudProject(id);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Projet retiré.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r, i) => { (g[r.module] ||= []).push({ ...r, _i: i }); });
    return g;
  }, [rows]);

  const patch = (idx, key, val) => {
    setDirty(true);
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  const toggleProject = (idx, pid) => {
    setDirty(true);
    setRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const cur = r.project_ids || [];
      return { ...r, project_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] };
    }));
  };

  const enabledCount = (m) => (grouped[m] || []).filter((r) => r.enabled).length;
  const aiCount = (m) => (grouped[m] || []).filter((r) => r.ai_indexing_enabled).length;

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        module: r.module, data_type: r.data_type, enabled: r.enabled,
        direction: r.direction, ai_indexing_enabled: r.ai_indexing_enabled,
        include_existing: r.include_existing, conflict_strategy: r.conflict_strategy,
        allowed_mime_types: r.allowed_mime_types || [], excluded_path_patterns: r.excluded_path_patterns || [],
        project_ids: r.project_ids || [],
      }));
      const res = await updateCloudPolicies("onedrive", payload);
      toast.success(`Politiques enregistrées (${res.saved} catégories).`);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const revokeAll = async () => {
    try {
      const res = await revokeCloudAi({ provider: "onedrive", reason: "Révocation globale IA" });
      toast.success(`Autorisation IA révoquée. ${res.ai_documents_deleted || 0} index IA supprimé(s), ${res.purged || 0} document(s) purgé(s).`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#EAB308] animate-spin" /></div>;
  }

  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid="cc-policies-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#EAB308]" /> Sélection & synchronisation
        </h2>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button data-testid="cc-ai-revoke-all" onClick={revokeAll}
              className="h-9 bg-transparent border border-[#dc143c]/40 text-[#ff8fa3] text-xs gap-1.5 hover:bg-[#dc143c]/10">
              <ShieldOff className="w-3.5 h-3.5" /> Révoquer l'IA (tout)
            </Button>
            <Button data-testid="cc-policies-save" onClick={save} disabled={!dirty || saving}
              className="h-9 bg-[#EAB308] text-black text-xs font-semibold gap-1.5 hover:opacity-90 disabled:opacity-40">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer
            </Button>
          </div>
        )}
      </div>
      <p className="text-sm text-white/50 mb-4">
        Choisissez précisément les modules, catégories, le sens de synchronisation, les règles de conflit et
        l'autorisation IA (choix distinct de la synchronisation).
      </p>

      {/* Gestion des projets */}
      <div className="mb-5 rounded-lg border border-white/10 bg-black/30 p-4" data-testid="cc-projects-card">
        <div className="flex items-center gap-2 mb-3">
          <FolderKanban className="w-4 h-4 text-[#EAB308]" />
          <h3 className="text-sm font-semibold text-white">Projets</h3>
          <span className="text-[11px] text-white/40">Limitez chaque catégorie à des projets précis (vide = tous)</span>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Input data-testid="cc-project-name" value={newProj.name}
              onChange={(e) => setNewProj((p) => ({ ...p, name: e.target.value }))}
              placeholder="Nom du projet" className="h-9 w-56 bg-black/50 border-white/15 text-white text-sm" />
            <Input data-testid="cc-project-code" value={newProj.code}
              onChange={(e) => setNewProj((p) => ({ ...p, code: e.target.value }))}
              placeholder="Code (optionnel)" className="h-9 w-40 bg-black/50 border-white/15 text-white text-sm" />
            <Button data-testid="cc-project-add" onClick={addProject} disabled={addingProj || !newProj.name.trim()}
              className="h-9 bg-white/10 border border-white/15 text-white text-xs gap-1.5 hover:bg-white/15">
              <Plus className="w-3.5 h-3.5" /> Ajouter
            </Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {projects.length === 0 && <span className="text-xs text-white/35">Aucun projet enregistré.</span>}
          {projects.map((p) => (
            <span key={p.id} data-testid={`cc-project-chip-${p.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/70">
              {p.name}{p.code ? ` · ${p.code}` : ""}
              {canManage && (
                <button onClick={() => removeProject(p.id)} data-testid={`cc-project-del-${p.id}`}
                  className="text-white/40 hover:text-[#dc143c]"><Trash2 className="w-3 h-3" /></button>
              )}
            </span>
          ))}
        </div>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {Object.keys(grouped).map((m) => (
          <AccordionItem key={m} value={m} data-testid={`cc-module-${m}`}
            className="rounded-lg border border-white/10 bg-black/30 px-3">
            <AccordionTrigger className="hover:no-underline py-3">
              <div className="flex items-center gap-3">
                <span className="font-bold text-white">{MODULE_LABELS[m] || m}</span>
                <span className="text-[11px] text-white/40">{enabledCount(m)} activées · {aiCount(m)} IA</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <div className="space-y-2">
                {grouped[m].map((r) => (
                  <div key={r.data_type} data-testid={`cc-cat-${m}-${r.data_type}`}
                    className="rounded-md border border-white/5 bg-black/40 p-3">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] items-center gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Switch checked={r.enabled} disabled={!canManage}
                        data-testid={`cc-enable-${m}-${r.data_type}`}
                        onCheckedChange={(v) => patch(r._i, "enabled", v)}
                        className="data-[state=checked]:bg-[#EAB308]" />
                      <span className="text-sm text-white/80 truncate">{r.data_type}</span>
                    </div>

                    <Select value={r.direction} disabled={!canManage || !r.enabled}
                      onValueChange={(v) => patch(r._i, "direction", v)}>
                      <SelectTrigger data-testid={`cc-dir-${m}-${r.data_type}`}
                        className="h-9 w-full md:w-[190px] bg-black/50 border-white/15 text-white/80 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/15 text-white">
                        {DIRECTIONS.map((d) => <SelectItem key={d.v} value={d.v} className="text-xs">{d.l}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <Select value={r.conflict_strategy} disabled={!canManage || !r.enabled}
                      onValueChange={(v) => patch(r._i, "conflict_strategy", v)}>
                      <SelectTrigger data-testid={`cc-conflict-${m}-${r.data_type}`}
                        className="h-9 w-full md:w-[210px] bg-black/50 border-white/15 text-white/80 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/15 text-white">
                        {CONFLICTS.map((c) => <SelectItem key={c.v} value={c.v} className="text-xs">{c.l}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    <label className="flex items-center gap-2 justify-end">
                      <Sparkles className={`w-3.5 h-3.5 ${r.ai_indexing_enabled ? "text-[#EAB308]" : "text-white/30"}`} />
                      <span className="text-[11px] text-white/50">IA</span>
                      <Switch checked={r.ai_indexing_enabled} disabled={!canManage || !r.enabled}
                        data-testid={`cc-ai-${m}-${r.data_type}`}
                        onCheckedChange={(v) => patch(r._i, "ai_indexing_enabled", v)}
                        className="data-[state=checked]:bg-[#dc143c]" />
                    </label>
                    </div>

                    {r.enabled && projects.length > 0 && (
                      <div className="mt-2 flex items-center gap-2 pl-1">
                        <span className="text-[11px] text-white/40">Projets :</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button data-testid={`cc-proj-select-${m}-${r.data_type}`}
                              className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[11px] text-white/70 hover:border-[#EAB308]/40">
                              {(r.project_ids || []).length === 0 ? "Tous les projets"
                                : `${(r.project_ids || []).length} projet(s)`}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 bg-[#0a0a0a] border-white/15 p-2">
                            <div className="space-y-1 max-h-52 overflow-y-auto">
                              {projects.map((p) => (
                                <label key={p.id} data-testid={`cc-proj-opt-${m}-${r.data_type}-${p.id}`}
                                  className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-white/5 cursor-pointer">
                                  <Checkbox checked={(r.project_ids || []).includes(p.id)} disabled={!canManage}
                                    onCheckedChange={() => toggleProject(r._i, p.id)}
                                    className="border-white/30 data-[state=checked]:bg-[#EAB308] data-[state=checked]:border-[#EAB308]" />
                                  <span className="text-xs text-white/75">{p.name}{p.code ? ` · ${p.code}` : ""}</span>
                                </label>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
